import { MarketingEntryPoint } from "@redotech/redo-model/advanced-flow/schemas/marketing/marketing";
import { MarketingForgeElementType } from "@redotech/redo-model/forge/marketing-signup-forge-types";
import { SignupFormData } from "@redotech/redo-model/marketing/schemas/submit-signup-form-step-schema";
import { SignupForm } from "@redotech/redo-model/signup-form-schema";
import { ANONYMOUS_SHOPPER_ID_KEY } from "../../utils/shared-conf";
import { SignupFormClient, SignupFormLocalData } from "./types";
export function isOnMobile(): boolean {
  if ((window.navigator as any).userAgentData?.mobile) return true;
  if (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    )
  )
    return true;
  return window.innerWidth < 768;
}

export function generateAnonymousCustomerId() {
  return `anonymous-${Math.random().toString(36).substring(2)}`;
}

export function parseLocalData(data: string): SignupFormLocalData {
  try {
    const raw = JSON.parse(data);
    return {
      customerHash: raw.customerHash,
      formAutoOpenedAt: raw.formAutoOpenedAt
        ? new Date(raw.formAutoOpenedAt)
        : undefined,
      formViewedAt: raw.formViewedAt ? new Date(raw.formViewedAt) : undefined,
      atLeastOneStepSubmittedAt: raw.atLeastOneStepSubmittedAt
        ? new Date(raw.atLeastOneStepSubmittedAt)
        : undefined,
      formId: raw.formId,
    };
  } catch (e) {
    console.error("Failed to parse signup form localStorage", e);
    return {};
  }
}

export const getNextStepIndex = (
  form: SignupForm | null,
  skipPhoneSteps: boolean,
  startIndex: number,
): number => {
  if (!form) return startIndex;
  let stepIndex = startIndex;

  while (
    skipPhoneSteps &&
    stepIndex < form.steps.length &&
    form.steps[stepIndex]?.elements.some(
      (el) =>
        el.type === MarketingForgeElementType.INPUT && el.inputType === "phone",
    )
  ) {
    stepIndex++;
  }

  return stepIndex;
};

export const submitStep = async ({
  i,
  formData,
  form,
  formId,
  client,
  customerId,
  localData,
  setCustomerId,
  logAnalytics,
  setCurrentStep,
  setOpen,
  setCompleted,
  entryPoint,
  skipPhoneSteps,
}: {
  i: number;
  formData: Record<string, string>;
  form: SignupForm | null;
  formId: string | null;
  client: SignupFormClient | undefined;
  customerId: string | null;
  localData: SignupFormLocalData;
  setCustomerId: (customerId: string) => void;
  logAnalytics: (type: "view" | "submit") => Promise<void>;
  setCurrentStep: (step: number) => void;
  setOpen: (open: boolean) => void;
  setCompleted: (completed: boolean) => void;
  entryPoint: MarketingEntryPoint;
  skipPhoneSteps: boolean;
}) => {
  if (!form || !formId || !client) return;
  const transformedFormData: SignupFormData = {};
  for (const [key, value] of Object.entries(formData)) {
    if (key === "email" || key === "phone") {
      transformedFormData[key] = value;
    } else if (key === "birthday") {
      const [month, day, year] = value.split("-").map((x) => parseInt(x));
      transformedFormData.birthday = new Date(
        Temporal.PlainDate.from({ year, month, day }).toString(),
      );
    }
  }
  const result = await client.submitSignupFormStep({
    formId,
    stepIndex: i,
    formData: transformedFormData,
    customerId: customerId ?? null,
    entryPoint,
    anonymousId: localStorage.getItem(ANONYMOUS_SHOPPER_ID_KEY)!,
  });

  if (!result.success) {
    console.error("Failed to submit form step", result);
    return;
  }

  if (result.customerId) {
    setCustomerId(result.customerId);
  }

  if (!localData.atLeastOneStepSubmittedAt && localData.customerHash) {
    await logAnalytics("submit");
  }

  const nextStepIndex = getNextStepIndex(form, skipPhoneSteps, i + 1);
  const hasNextStep = nextStepIndex < form.steps.length;

  if (hasNextStep) {
    setCurrentStep(nextStepIndex);
  } else {
    setCurrentStep(0);
    setOpen(false);
    setCompleted(true);
  }
};
